import { SELF_SERVE_TRIAL_DAYS } from '@a7y/types/billing';
import { APPTONOMY_FOUNDED_YEAR } from './lib/content-base';
import { ENGINE_COUNT_WORD, LANDING_SEO, START_SEO } from './lib/wedge';
import { ENGINE_CONTENT } from './lib/content-engines';
import { CALC_SEO } from './lib/aso-calculator';
import { SECURITY_PUBLICATION_ENABLED } from './security-launch-control.vite';

export const SITE_NAME = 'Apptonomy';
export const BASE_URL = 'https://www.apptonomy.ai';

export const DEFAULT_OG_IMAGE = `${BASE_URL}/assets/img/brand/og-default.png`;
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

export interface PageSeoMeta {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  /** Optional OG title override. Falls back to `title` when not set. */
  ogTitle?: string;
  /** Optional OG description override. Falls back to `description` when not set. */
  ogDescription?: string;
}

export const SEO_METADATA: Record<string, PageSeoMeta> = {
  '/': {
    title: LANDING_SEO.title,
    description: LANDING_SEO.description,
    canonical: `${BASE_URL}/`,
    ogTitle: LANDING_SEO.ogTitle,
    ogDescription: LANDING_SEO.ogDescription,
  },
  '/pricing': {
    title: 'Pricing — Apptonomy',
    description:
      `Apptonomy plans and prices in EUR, USD and GBP: Free, Basic, Pro, Elite and Enterprise. Basic, Pro and Elite start with a ${SELF_SERVE_TRIAL_DAYS}-day trial and carry a 30-day money-back guarantee.`,
    canonical: `${BASE_URL}/pricing`,
  },
  '/how-it-works': {
    title: 'How It Works — Apptonomy',
    description:
      'How Apptonomy works: connect an app, audit it on the schedule the plan sets, review one ranked revision plan, then send the changes to both stores and see the movement after each one.',
    canonical: `${BASE_URL}/how-it-works`,
  },
  '/methodology': {
    title: 'Methodology — Apptonomy',
    description:
      `The ${ENGINE_COUNT_WORD.toLowerCase()} Apptonomy audit engines, the data each one reads, and how language models apply a written ASO methodology. Each engine's methodology is published.`,
    canonical: `${BASE_URL}/methodology`,
  },
  '/about': {
    title: 'About — Apptonomy',
    description:
      `Apptonomy is built by Yevhen Tarasenko, an ASO consultant, and Thomas Purnell-Fisher, a product and engineering leader. In development since ${APPTONOMY_FOUNDED_YEAR}.`,
    canonical: `${BASE_URL}/about`,
  },
  '/affiliates': {
    title: 'Affiliate Program — Apptonomy',
    description:
      'Earn 30% recurring commission for referring app teams to Apptonomy — every month, for up to a year per customer. Apply to join the Apptonomy Affiliate Program.',
    canonical: `${BASE_URL}/affiliates`,
  },
  '/roadmap': {
    title: 'Roadmap — Apptonomy',
    description:
      "The loop runs end-to-end today. See what's live, what's in development, and what's on the horizon on the road to closed-loop ASO autonomy.",
    canonical: `${BASE_URL}/roadmap`,
  },
  '/release-notes': {
    title: 'Release Notes — Apptonomy',
    description:
      "See what's new in Apptonomy. Track new features, improvements, and fixes to our ASO intelligence platform.",
    canonical: `${BASE_URL}/release-notes`,
  },
  '/contact': {
    title: 'Contact — Apptonomy',
    description:
      'Contact Apptonomy with a question about the product, an account or a partnership, or about Enterprise plans for agencies and app portfolios.',
    canonical: `${BASE_URL}/contact`,
  },
  '/refund-policy': {
    title: 'Refund Policy — Apptonomy',
    description:
      'Every paid Apptonomy plan is backed by a 30-day money-back guarantee. Cancel within 30 days of your first charge for a full refund.',
    canonical: `${BASE_URL}/refund-policy`,
  },
  '/terms': {
    title: 'Terms of Service — Apptonomy',
    description:
      "The legal terms governing your access to and use of Apptonomy's ASO intelligence platform, including subscriptions, payment, and acceptable use.",
    canonical: `${BASE_URL}/terms`,
  },
  '/privacy': {
    title: 'Privacy Policy — Apptonomy',
    description:
      'How Apptonomy collects, uses, shares, and protects your personal information, and the privacy rights available to you depending on where you live.',
    canonical: `${BASE_URL}/privacy`,
  },
  '/cookie-policy': {
    title: 'Cookie Policy — Apptonomy',
    description:
      'How Apptonomy uses cookies and similar technologies, what each cookie does, and how you can control your cookie preferences.',
    canonical: `${BASE_URL}/cookie-policy`,
  },
  // `/signup` is intentionally NOT prerendered: the page is a Clerk-owned
  // sign-up widget. A prerendered shell is just a modal scrim with no
  // crawlable content, so it stays a lazy <Route> in App.tsx (same rationale
  // as `/schedule-demo`).
  '/appcheck': {
    title: 'App Check — Apptonomy',
    description:
      "Check your app's ASO readiness with a quick analysis of your App Store or Google Play listing.",
    canonical: `${BASE_URL}/appcheck`,
  },
  // ---------------------------------------------------------------------------
  // Persona (/for/*) pages
  // ---------------------------------------------------------------------------
  '/for/agencies': {
    title: 'Apptonomy for Agencies',
    description:
      "An agency runs the Apptonomy audit on each client's app and gives the client the PDF report, with the reason behind each recommendation.",
    canonical: `${BASE_URL}/for/agencies`,
  },
  '/for/growth-teams': {
    title: 'Apptonomy for Growth Teams',
    description:
      'A growth team reads Apptonomy audits beside daily App Store Connect and Google Play Console analytics, in the same project.',
    canonical: `${BASE_URL}/for/growth-teams`,
  },
  '/for/publishers': {
    title: 'Apptonomy for Publishers',
    description:
      'A publisher without an ASO specialist runs the Apptonomy audit and works through ranked listing changes, each with its reason.',
    canonical: `${BASE_URL}/for/publishers`,
  },
  // SEO copy refined for A7Y-1835.
  '/for/consultants': {
    title: 'Apptonomy for Consultants',
    description:
      "A freelance consultant uses the Apptonomy audit as the analysis's first pass; every recommendation carries its reasoning.",
    canonical: `${BASE_URL}/for/consultants`,
  },
  // ---------------------------------------------------------------------------
  // Comparison pages
  // ---------------------------------------------------------------------------
  // SEO copy refined for A7Y-1835.
  '/vs/apptweak': {
    title: 'Apptonomy vs. AppTweak: Dashboards or a Weekly Revision Plan',
    description:
      'AppTweak hands your analyst dashboards. Apptonomy converges four daily streams into one Weekly Revision Plan and publishes to iOS and Google Play in one click.',
    canonical: `${BASE_URL}/vs/apptweak`,
  },
  '/diy-llm-vs-apptonomy': {
    title: 'Can’t I Just Do This With Claude? — Apptonomy',
    description:
      'You’d get a good first answer — then comes day two. A full audit runs 10 engines against your app and its competitors, on the schedule your plan sets.',
    canonical: `${BASE_URL}/diy-llm-vs-apptonomy`,
  },
  // ---------------------------------------------------------------------------
  // Platform capability pages
  // ---------------------------------------------------------------------------
  // SEO copy refined for A7Y-1835.
  '/features/publishing': {
    title: 'One-Click Publishing — Apptonomy',
    description:
      'The Weekly Revision Plan converges every recommendation into one artifact. Approve, edit, or decline per row, then publish to iOS and Google Play in one click.',
    canonical: `${BASE_URL}/features/publishing`,
  },
  '/features/analytics': {
    title: 'ASO Analytics — Apptonomy',
    description:
      'App Store Connect and Play Console ingestion ties every shipped change to install and conversion movement. Score History and Recommendation Impact, on your data.',
    canonical: `${BASE_URL}/features/analytics`,
  },
  // ---------------------------------------------------------------------------
  // Per-engine methodology pages
  // ---------------------------------------------------------------------------
  '/engines/ai-discoverability': {
    title: 'AI Discoverability Engine — Apptonomy',
    description:
      ENGINE_CONTENT['ai-discoverability'].summary,
    canonical: `${BASE_URL}/engines/ai-discoverability`,
  },
  '/engines/keywords-and-semantic-clusters': {
    title: 'Keywords & Semantic Clusters Engine — Apptonomy',
    description:
      ENGINE_CONTENT['keywords-and-semantic-clusters'].summary,
    canonical: `${BASE_URL}/engines/keywords-and-semantic-clusters`,
  },
  '/engines/user-intent': {
    title: 'User Intent Engine — Apptonomy',
    description:
      ENGINE_CONTENT['user-intent'].summary,
    canonical: `${BASE_URL}/engines/user-intent`,
  },
  '/engines/store-text': {
    title: 'Store Text Engine — Apptonomy',
    description:
      ENGINE_CONTENT['store-text'].summary,
    canonical: `${BASE_URL}/engines/store-text`,
  },
  '/engines/screenshots': {
    title: 'Screenshots Engine — Apptonomy',
    description:
      ENGINE_CONTENT['screenshots'].summary,
    canonical: `${BASE_URL}/engines/screenshots`,
  },
  '/engines/icon': {
    title: 'Icon Engine — Apptonomy',
    description:
      ENGINE_CONTENT['icon'].summary,
    canonical: `${BASE_URL}/engines/icon`,
  },
  '/engines/sentiment': {
    title: 'User Sentiment Engine — Apptonomy',
    description:
      ENGINE_CONTENT['sentiment'].summary,
    canonical: `${BASE_URL}/engines/sentiment`,
  },
  '/engines/competitors': {
    title: 'Competitors Engine — Apptonomy',
    description:
      ENGINE_CONTENT['competitors'].summary,
    canonical: `${BASE_URL}/engines/competitors`,
  },
  '/engines/localization-analysis': {
    title: 'Localization Analysis Engine — Apptonomy',
    description:
      ENGINE_CONTENT['localization-analysis'].summary,
    canonical: `${BASE_URL}/engines/localization-analysis`,
  },
  '/engines/policy-checker': {
    title: 'Policy Checker Engine — Apptonomy',
    description:
      ENGINE_CONTENT['policy-checker'].summary,
    canonical: `${BASE_URL}/engines/policy-checker`,
  },
  // ---------------------------------------------------------------------------
  // /start — ASO on Rails, the product-first landing page (A7Y-5394; spec §4.1).
  // Indexed, canonical to itself; the root keeps its own canonical, title and
  // H1, so the two pages make no duplicate-content claim. OG falls back to the
  // page title/description and the default image until a dedicated one exists.
  // ---------------------------------------------------------------------------
  '/start': {
    title: START_SEO.title,
    description: START_SEO.description,
    canonical: `${BASE_URL}/start`,
  },
  // A7Y-6118: the ASO calculator.
  '/tools/calc': {
    title: CALC_SEO.title,
    description: CALC_SEO.description,
    canonical: `${BASE_URL}/tools/calc`,
  },
  ...(SECURITY_PUBLICATION_ENABLED
    ? {
        '/security': {
          title: 'Security at Apptonomy',
          description:
            'Apptonomy’s security pages and documents: store credential protection, our vendor-questionnaire answers, our independent-assurance roadmap, the DPA and subprocessors.',
          canonical: `${BASE_URL}/security`,
        },
        '/security/store-credentials': {
          title: 'Store Credential Security | Apptonomy',
          description:
            'How Apptonomy protects connected store data, credentials, AI inputs, and publishing access, including our current SOC 2 status.',
          canonical: `${BASE_URL}/security/store-credentials`,
        },
        '/security/riskguard': {
          title: 'RiskGuard Questionnaire Answers | Apptonomy',
          description:
            'A section-by-section summary of Apptonomy’s answers to the 88-question RiskGuard vendor-risk questionnaire.',
          canonical: `${BASE_URL}/security/riskguard`,
        },
        '/security/soc2': {
          title: 'SOC 2 status and roadmap | Apptonomy',
          description:
            'Where we stand on a SOC 2 examination, what already exists, and the steps to an independent report.',
          canonical: `${BASE_URL}/security/soc2`,
        },
        '/security/iso27001': {
          title: 'ISO/IEC 27001 status and roadmap | Apptonomy',
          description:
            'Our position against ISO/IEC 27001:2022, the management system we are building, and the path to certification.',
          canonical: `${BASE_URL}/security/iso27001`,
        },
        '/dpa': {
          title: 'Data Processing Agreement | Apptonomy',
          description:
            'Apptonomy’s current Data Processing Agreement for customers and processors.',
          canonical: `${BASE_URL}/dpa`,
        },
        '/subprocessors': {
          title: 'Subprocessors | Apptonomy',
          description:
            'The current list of subprocessors that may process personal data for Apptonomy.',
          canonical: `${BASE_URL}/subprocessors`,
        },
        '/security/advisories': {
          title: 'Security Advisories | Apptonomy',
          description:
            'Published security advisories for the Apptonomy service and how to subscribe to security notices.',
          canonical: `${BASE_URL}/security/advisories`,
        },
      }
    : {}),
};
